import {
  Add,
  Delete,
  Edit,
  Remove,
  SyncAlt,
  Visibility,
} from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import {
  createIncomeOrExpenseAddRoute,
  createIncomeOrExpenseEditRoute,
  createIncomeOrExpenseViewRoute,
} from 'adminConstants/routes';
import Page from 'components/new/Page';
import PageFilters, { Filter, FiltersGroup } from 'components/new/PageFilters';
import { useLayoutContext } from 'contexts/LayoutContext';
import { IncomeOrExpenseType } from 'further-types/lp/income-and-expenses';
import { CalendarDayRange } from 'further-types/utils';
import BasicSelect from 'further-ui/components/BasicSelect';
import ColorIndicator from 'further-ui/components/ColorIndicator';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import DateRangePicker from 'further-ui/components/DateRangePicker';
import { Column, Row } from 'further-ui/components/Layout';
import Menu from 'further-ui/components/Menu';
import SearchField from 'further-ui/components/SearchField';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { useDisclosure, usePaginatedFilters } from 'further-ui/hooks';
import { ActionStatusLabels } from 'further-ui/labels';
import { numberToCurrencyString, stringToColor } from 'further-ui/utils';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { useHistory } from 'react-router-dom';

const pageId = 'income-and-expenses';

const actionTypeOptions = [
  { label: 'All', value: null },
  { label: 'Income', value: IncomeOrExpenseType.Income },
  { label: 'Expense', value: IncomeOrExpenseType.Expense },
];

type Props = {
  vehicleId: string;
};
const ListIncomeAndExpenses: React.FC<Props> = ({ vehicleId }) => {
  const history = useHistory();
  const deleteDisclosure = useDisclosure();
  const { vehicle } = useLayoutContext();

  const { pagination, filters, handleChangeFilters, isFiltersChangePending } =
    usePaginatedFilters<
      {
        searchQuery: string;
        actionType: IncomeOrExpenseType | null;
        dateRange: CalendarDayRange | null;
      },
      'startDate'
    >(pageId, {
      defaultFilters: {
        searchQuery: '',
        actionType: null,
        dateRange: null,
      },
      defaultOrder: {
        orderBy: 'startDate',
        order: 'desc',
      },
    });

  const [data] = api.incomeAndExpenses.list.useSuspenseQuery({
    vehicleId,
    searchQuery: filters.searchQuery,
    actionType: filters.actionType ?? undefined,
    startDate: filters.dateRange?.startDate ?? undefined,
    endDate: filters.dateRange?.endDate ?? undefined,
    ...pagination.properties,
  });

  const deleteIncomeOrExpense = api.incomeAndExpenses.remove.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Income or expense deleted successfully',
      onSuccess: () => {
        deleteDisclosure.onClose();
      },
    }),
  );

  const handleDeleteIncomeOrExpense = () => {
    deleteIncomeOrExpense.mutate({
      id: deleteDisclosure.actionProps.id,
      vehicleId,
    });
  };

  const columns: Array<TableColumn<(typeof data.results)[number]>> = [
    {
      label: 'Counterparty',
      key: 'counterparty',
      render: ({ counterparty, status }) => (
        <Row spacing="sm" centered>
          <ColorIndicator color={stringToColor(counterparty)} />
          <Column>
            <Typography variant="h6">{counterparty}</Typography>
            <Typography variant="body2">
              {ActionStatusLabels[status]}
            </Typography>
          </Column>
        </Row>
      ),
    },
    {
      label: 'Created date',
      key: 'createdDate',
      render: (row) => row.createdDate.toReadable(),
    },
    {
      label: 'Ledger date',
      key: 'startDate',
      render: (row) => row.startDate.toReadable(),
    },
    {
      label: 'Action type',
      key: 'actionType',

      render: (row) =>
        row.actionType === IncomeOrExpenseType.Income ? 'Income' : 'Expense',
    },

    {
      label: 'Amount',
      key: 'amount',
      render: (row) =>
        numberToCurrencyString(
          row.actionType === IncomeOrExpenseType.Income
            ? row.amount
            : -row.amount,
          {
            currency: vehicle?.baseCurrency,
          },
        ),
    },
  ];

  return (
    <Page
      spacing="lg"
      heading="Income and expenses"
      headingIcon={SyncAlt}
      withHeadingDivider
      customActions={
        <Menu
          label="Add new"
          buttonVariant="contained"
          items={[
            {
              label: 'Income',
              icon: Add,
              onClick: () => {
                history.push(
                  createIncomeOrExpenseAddRoute(IncomeOrExpenseType.Income),
                );
              },
            },
            {
              label: 'Expense',
              icon: Remove,
              onClick: () => {
                history.push(
                  createIncomeOrExpenseAddRoute(IncomeOrExpenseType.Expense),
                );
              },
            },
          ]}
        />
      }
    >
      <Column spacing="sm">
        <PageFilters justify="space-between">
          <Filter size="md">
            <SearchField
              autoSearchOnDebounce
              name="searchQuery"
              placeholder="Search by deal name"
              fullWidth
              value={filters.searchQuery}
              onSearch={(searchQuery) => {
                handleChangeFilters({ searchQuery });
              }}
              allowEmptyQuery
              iconPlacement="left"
            />
          </Filter>
          <FiltersGroup justify="flex-end">
            <Filter size="sm" justify="flex-end">
              <BasicSelect
                fullWidth
                options={actionTypeOptions}
                value={filters.actionType}
                onChange={(newValue) => {
                  handleChangeFilters({ actionType: newValue });
                }}
                placeholder="Filter by type"
              />
            </Filter>
            <Filter size="sm">
              <DateRangePicker
                dateRange={filters.dateRange ?? undefined}
                onDateRangeChange={(dateRange) => {
                  handleChangeFilters({ dateRange });
                }}
                placeholder="Select date range"
              />
            </Filter>
          </FiltersGroup>
        </PageFilters>
        <Table
          columns={columns}
          isLoading={isFiltersChangePending}
          pagination={pagination}
          rows={data?.results ?? []}
          rowsTotalCount={data?.totalCount ?? 0}
          emptyMessage="No income or expenses found."
          actions={[
            {
              label: 'Edit',
              icon: Edit,
              link: (row) => createIncomeOrExpenseEditRoute(row.id),
              disabled: (row) => !row.canModify,
              tooltip: (row) =>
                !row.canModify ? row.canModifyReason : undefined,
            },
            {
              label: 'View',
              icon: Visibility,
              link: (row) => createIncomeOrExpenseViewRoute(row.id),
            },
            {
              label: 'Delete',
              icon: Delete,
              onClick: (row) => deleteDisclosure.stageAction({ id: row.id }),
              disabled: (row) => !row.canModify,
              tooltip: (row) =>
                !row.canModify ? row.canModifyReason : undefined,
            },
          ]}
        />
      </Column>

      <ConfirmDialog
        onClose={deleteDisclosure.onClose}
        open={deleteDisclosure.isOpen}
        title="Are you sure you wish to delete this action?"
        content="This cannot be undone and all information relating to the action will be lost. If you have draft equalisations they will also be deleted."
        loading={deleteIncomeOrExpense.isPending}
        maxWidth="sm"
        actions={{
          confirm: {
            label: 'Delete income or expense',
            onClick: handleDeleteIncomeOrExpense,
          },
          cancel: {
            label: 'Go back',
            onClick: deleteDisclosure.onClose,
          },
        }}
      />
    </Page>
  );
};

const ListIncomeAndExpensesWrapper: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();

  if (!selectedVehicleId) {
    return null;
  }

  return <ListIncomeAndExpenses vehicleId={selectedVehicleId} />;
};

export default ListIncomeAndExpensesWrapper;
